import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Share2, Trash2, Check, UploadCloud } from 'lucide-react';
import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import BottomBar from './components/BottomBar';
import NoteComposeModal from './components/NoteComposeModal';
import TakeNote from './components/TakeNote';
import NoteCard from './components/NoteCard';
import NoteDetailModal from './components/NoteDetailModal';
import EmptyState from './components/EmptyState';
import AuthCallback from './components/AuthCallback';
import LoginScreen from './components/LoginScreen';
import PublicNoteView from './components/PublicNoteView';
import PublicCollectionView from './components/PublicCollectionView';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { ThemeProvider } from './lib/ThemeContext';
import { LanguageProvider, useT } from './lib/LanguageContext';
import { shareCollection, extractUrls, normalizeUrl } from './lib/utils';
import {
  fetchDocuments,
  fetchDocument,
  ingestNote,
  searchBrain,
  deleteDocument,
  listCategories,
  listProjects,
} from './lib/api';
import {
  readCachedDocs,
  readCachedCursor,
  readCachedCounts,
  writeCachedDocs,
  isCacheStale,
} from './lib/docCache';

// Sidebar filter is one of 'all' | 'cat:<id>' | 'proj:<id>' | '<source_type>'.
// Only the category/project forms are something the search API can scope on.
const categoryIdOf = (f) => (f?.startsWith('cat:') ? f.slice(4) : null);
const projectIdOf = (f) => (f?.startsWith('proj:') ? f.slice(5) : null);

function AppShell() {
  const { user } = useAuth();
  const t = useT();
  // Seed from localStorage so a reload paints the grid immediately instead of
  // spinning through a `/documents` round-trip.
  const [docs, setDocs] = useState(readCachedDocs);
  const [pendingDocs, setPendingDocs] = useState([]); // optimistic in-flight ingests
  const [loading, setLoading] = useState(() => readCachedDocs().length === 0);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  // Paginated feed: `docs` holds every page loaded so far for `feedFilter`
  // (the filter those pages were fetched with — it lags `filter` while a
  // refetch is in flight), and `nextCursor` is null once the last page is in.
  const [feedFilter, setFeedFilter] = useState('all');
  const [nextCursor, setNextCursor] = useState(readCachedCursor);
  const [loadingMore, setLoadingMore] = useState(false);
  const [counts, setCounts] = useState(readCachedCounts);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  // Bumped on every first-page load so a slower page from the previous filter
  // (or a loadMore racing a refresh) can't land on top of the new feed.
  const feedTokenRef = useRef(0);
  const sentinelRef = useRef(null);
  // Local mutations nudge the server's library-wide counts until the next fetch.
  const bumpCount = useCallback((sourceType, delta) => {
    if (!sourceType) return;
    setCounts((prev) => {
      const next = { ...(prev || {}) };
      next[sourceType] = Math.max(0, (next[sourceType] || 0) + delta);
      return next;
    });
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeOrigin, setComposeOrigin] = useState(null);
  const [openedDoc, setOpenedDoc] = useState(null);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const desktopTakeNoteRef = useRef(null);

  // ---- Multi-select (checkbox click or double-click-drag marquee) -------
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCopied, setBulkCopied] = useState(false);
  const selectMode = selectedIds.size > 0;

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Paste/drop-anywhere: a screenshot, a link, or a file dropped from the OS
  // file explorer lands straight into the (desktop) note composer without
  // first clicking "Take a note" — same idea as Notion/Figma's global paste.
  // Guarded so it never steals a paste/drop that's meant for some OTHER
  // focused input (search box, an open note's edit fields, a rename prompt,
  // the mobile compose modal which already handles its own paste locally).
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    const isEditableTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    // The always-mounted desktop composer this all targets is only visible
    // at the md breakpoint (`hidden md:block`) — below that, the mobile
    // compose modal (which handles its own local paste/drop) is the real UI.
    const isDesktopWidth = () => window.innerWidth >= 768;
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');

    const onPaste = (e) => {
      if (composeOpen || !isDesktopWidth() || isEditableTarget(document.activeElement)) return;
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter((it) => /^image\//.test(it.type));
      if (imageItems.length > 0) {
        e.preventDefault();
        const files = imageItems.map((it) => it.getAsFile()).filter(Boolean);
        if (files.length) desktopTakeNoteRef.current?.stageExternalImages(files);
        return;
      }
      const text = (e.clipboardData?.getData('text/plain') || '').trim();
      if (!text) return;
      const urls = extractUrls(text);
      // A paste that's ENTIRELY a link (the common "copied a URL" case) —
      // stage it as its own chip. Otherwise (a sentence with a link inside
      // it, or no link at all) leave it alone — that's not an unambiguous
      // "save this link" gesture, and there's no focused field to drop it
      // into anyway when nothing was clicked first.
      if (urls.length === 1 && text.replace(urls[0], '').trim() === '') {
        e.preventDefault();
        desktopTakeNoteRef.current?.stageExternalLink(normalizeUrl(urls[0]));
      }
    };

    const onDragEnter = (e) => {
      if (composeOpen || !isDesktopWidth() || !hasFiles(e)) return;
      dragCounterRef.current += 1;
      setDragActive(true);
    };

    const onDragOver = (e) => {
      if (composeOpen || !isDesktopWidth() || !hasFiles(e)) return;
      e.preventDefault();
    };

    const onDragLeave = () => {
      if (dragCounterRef.current === 0) return;
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) setDragActive(false);
    };

    const onDrop = (e) => {
      dragCounterRef.current = 0;
      setDragActive(false);
      if (composeOpen || !isDesktopWidth()) return;
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      e.preventDefault();
      const images = files.filter((f) => /^image\//.test(f.type));
      const pdfs = files.filter((f) => f.type === 'application/pdf');
      if (images.length) desktopTakeNoteRef.current?.stageExternalImages(images);
      if (pdfs.length) desktopTakeNoteRef.current?.stageExternalPdfs(pdfs);
    };

    document.addEventListener('paste', onPaste);
    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, [composeOpen]);

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(t('home.bulkDeleteConfirm', { n: ids.length, s: ids.length > 1 ? 's' : '' }))) return;
    try {
      await Promise.all(ids.map((id) => deleteDocument(id)));
    } catch (e) {
      console.warn('bulk delete partial failure', e);
    }
    docs.filter((d) => selectedIds.has(d.id)).forEach((d) => bumpCount(d.source_type, -1));
    setDocs((prev) => prev.filter((d) => !selectedIds.has(d.id)));
    setSearchResults((prev) => (prev ? prev.filter((r) => !selectedIds.has(r.doc_id)) : prev));
    setSelectedIds(new Set());
  };

  const handleBulkShare = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !user?.sub) return;
    try {
      const result = await shareCollection(user.sub, ids, t);
      if (result === 'copied') {
        setBulkCopied(true);
        setTimeout(() => setBulkCopied(false), 1500);
      }
    } catch (e) {
      console.warn('bulk share failed', e);
    }
  };

  // Rubber-band selection: click(/tap)-and-drag draws a box; any note card
  // it intersects gets selected. No extra "select mode" button needed.
  // On mouse, ANY press-and-drag over non-interactive space arms it
  // immediately — desktop has no scroll-via-drag to protect, so there's no
  // reason to gate it behind a double click, and this is what keeps native
  // text selection from ever winning the drag. On touch, a single-finger
  // drag has to still be able to scroll, so only a second tap that's
  // actually HELD (long-press) arms selection there. Armed from anywhere on
  // the page (cards, gaps, margins) except actual controls, which keep
  // their normal click / text-select behavior.
  const dragStateRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const lastPointerDownRef = useRef(null);
  const holdTimerRef = useRef(null);
  const [dragBox, setDragBox] = useState(null);

  const rectsIntersect = (a, b) =>
    a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

  const handleGridPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest?.('button, a, input, textarea, select, [contenteditable="true"], [data-testid="sidebar"]')) return;

    const startX = e.clientX;
    const startY = e.clientY;

    const arm = () => {
      // Without this, the drag hijacks the browser's native word/text
      // selection instead of drawing our marquee box. selectstart is the
      // direct hook for that; preventDefault alone isn't reliably enough.
      window.getSelection()?.removeAllRanges();
      document.body.style.userSelect = 'none';
      const blockSelectStart = (ev) => ev.preventDefault();
      document.addEventListener('selectstart', blockSelectStart);
      dragStateRef.current = { startX, startY, dragging: false, blockSelectStart };
      navigator.vibrate?.(15);
    };

    const onMove = (ev) => {
      const state = dragStateRef.current;
      if (!state) return;
      const dx = ev.clientX - state.startX;
      const dy = ev.clientY - state.startY;
      if (!state.dragging && Math.hypot(dx, dy) < 6) return;
      ev.preventDefault?.();
      state.dragging = true;
      const box = {
        left: Math.min(state.startX, ev.clientX),
        top: Math.min(state.startY, ev.clientY),
        right: Math.max(state.startX, ev.clientX),
        bottom: Math.max(state.startY, ev.clientY),
      };
      setDragBox(box);
      const els = Array.from(document.querySelectorAll('[data-note-id]'));
      const hitIds = els
        .filter((el) => rectsIntersect(el.getBoundingClientRect(), box))
        .map((el) => el.getAttribute('data-note-id'));
      setSelectedIds(new Set(hitIds));
    };

    // Declared up front (not just in the touch branch below) so cleanup()
    // always has an initialized reference to remove, whichever branch ran.
    let onPreArmMove = () => {};

    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointermove', onPreArmMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      clearTimeout(holdTimerRef.current);
      const state = dragStateRef.current;
      if (state) {
        document.removeEventListener('selectstart', state.blockSelectStart);
        document.body.style.userSelect = '';
        if (state.dragging) suppressNextClickRef.current = true;
      }
      dragStateRef.current = null;
      setDragBox(null);
    };

    const onUp = () => cleanup();

    if (e.pointerType === 'mouse') {
      e.preventDefault();
      arm();
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      return;
    }

    // Touch/pen: gate behind a double-tap where the second tap is HELD —
    // a plain single-finger drag still needs to scroll the page.
    const now = Date.now();
    const prev = lastPointerDownRef.current;
    lastPointerDownRef.current = { time: now, x: startX, y: startY };
    const isSecondTap = prev && now - prev.time < 450 && Math.hypot(startX - prev.x, startY - prev.y) < 12;
    if (!isSecondTap) return;
    lastPointerDownRef.current = null;

    onPreArmMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.hypot(dx, dy) > 10) cleanup(); // moved too soon — treat as a scroll, not a hold
    };
    window.addEventListener('pointermove', onPreArmMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    holdTimerRef.current = setTimeout(() => {
      window.removeEventListener('pointermove', onPreArmMove);
      arm();
      // The hold itself enters selection mode, marking whatever's under the
      // finger right now — dragging from here just extends the selection.
      const initialCard = document.elementFromPoint(startX, startY)?.closest('[data-note-id]');
      if (initialCard) setSelectedIds(new Set([initialCard.getAttribute('data-note-id')]));
      window.addEventListener('pointermove', onMove);
    }, 400);
  };

  // Swallow the click that follows a completed drag so it doesn't also
  // open whichever card the mouse happened to release over.
  const handleGridClickCapture = (e) => {
    if (suppressNextClickRef.current) {
      e.stopPropagation();
      e.preventDefault();
      suppressNextClickRef.current = false;
    }
  };

  const loadCategories = useCallback(async () => {
    try {
      const r = await listCategories();
      setCategories(r.categories || []);
    } catch {}
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const r = await listProjects();
      setProjects(r.projects || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadCategories();
    loadProjects();
  }, [loadCategories, loadProjects]);

  // search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = no search active
  const [searching, setSearching] = useState(false);

  const debounceRef = useRef(null);

  // (Re)load the FIRST page of the current filter's feed, dropping any pages
  // loaded after it — the same as pulling to refresh on a social feed.
  const loadDocs = useCallback(async (silent = false) => {
    const token = ++feedTokenRef.current;
    const f = filterRef.current;
    try {
      if (!silent) setLoading(true);
      const d = await fetchDocuments({ filter: f });
      if (feedTokenRef.current !== token) return;
      const fresh = d.documents || [];
      setDocs(fresh);
      setFeedFilter(f);
      setNextCursor(d.next_cursor || null);
      setCounts(d.counts || null);
      if (f === 'all') {
        writeCachedDocs(fresh, { nextCursor: d.next_cursor || null, counts: d.counts || null });
      }
    } catch (e) {
      console.warn('loadDocs failed', e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    const token = feedTokenRef.current;
    setLoadingMore(true);
    try {
      const d = await fetchDocuments({ filter: feedFilter, cursor: nextCursor });
      if (feedTokenRef.current !== token) return;
      const more = d.documents || [];
      setDocs((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...more.filter((x) => !seen.has(x.id))];
      });
      setNextCursor(d.next_cursor || null);
    } catch (e) {
      console.warn('loadMore failed', e);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, feedFilter]);

  // First mount: only hit the backend when the cached snapshot has gone stale —
  // an ordinary reload within the TTL renders from cache and makes no request
  // at all. After that, every filter change fetches that filter's first page
  // (filters run server-side so each page comes back full). The Refresh button
  // still calls loadDocs directly.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (isCacheStale()) loadDocs(readCachedDocs().length > 0);
      return;
    }
    loadDocs(true);
  }, [filter, loadDocs]);

  // Keep the cache in step with local mutations (delete, inline edit, ingest,
  // loadMore) without restarting the freshness TTL — those changes are already
  // applied server-side, they just aren't a new read of the first page. A
  // filtered feed is never cached: the cache is what the unfiltered home shows.
  useEffect(() => {
    if (feedFilter !== 'all') return;
    writeCachedDocs(docs, { fetched: false, nextCursor, counts });
  }, [docs, feedFilter, nextCursor, counts]);

  // Infinite scroll: fetch the next page once the sentinel under the grid gets
  // near the viewport. Paused during a search — those results come from the
  // search API (which finds every note, loaded or not), not from the feed.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !nextCursor || searchResults !== null) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: '800px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [nextCursor, searchResults, loadMore]);

  // ---- Background re-index polling --------------------------------------
  // While a doc is still being processed by the backend, poll THAT doc every
  // 3s so the UI flips the shimmer card → finalised card automatically. Only
  // the pending docs are polled — re-listing the whole library each tick to
  // watch one card finish is the most wasteful call the app can make.
  useEffect(() => {
    const pending = docs.filter((d) => d.status === 'processing').map((d) => d.id);
    if (pending.length === 0) return undefined;

    const t = setInterval(async () => {
      const settled = await Promise.allSettled(pending.map((id) => fetchDocument(id)));
      const done = settled
        .filter((r) => r.status === 'fulfilled' && r.value?.status !== 'processing')
        .map((r) => r.value);
      if (done.length === 0) return;
      setDocs((prev) => {
        const byId = new Map(done.map((d) => [d.id, d]));
        return prev.map((d) => byId.get(d.id) || d);
      });
    }, 3000);
    return () => clearInterval(t);
  }, [docs]);

  // Library-wide counts come from the server — the loaded pages are a slice.
  const sourceCounts = useMemo(
    () => ({ text: 0, url: 0, youtube: 0, pdf: 0, image: 0, ...(counts || {}) }),
    [counts],
  );

  // Bumped on every runSearch call (including a clear) so an in-flight
  // fetch can tell it's been superseded — without this, clearing the box
  // while a previous search is still resolving lets that stale response
  // land after the clear and repopulate searchResults/searching from it.
  const searchTokenRef = useRef(0);

  const runSearch = async (q, categoryId = categoryIdOf(filter), projectId = projectIdOf(filter)) => {
    const token = ++searchTokenRef.current;
    if (!q) {
      setSearchQuery('');
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearchQuery(q);
    setSearching(true);
    try {
      const res = await searchBrain(q, 12, categoryId, projectId);
      if (searchTokenRef.current !== token) return;
      setSearchResults(res.results || []);
    } catch (e) {
      console.warn('search failed', e);
      if (searchTokenRef.current !== token) return;
      setSearchResults([]);
    } finally {
      if (searchTokenRef.current === token) setSearching(false);
    }
  };

  // Debounce search so user can type without flooding the API
  const onSearch = (q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q) {
      runSearch('');
      return;
    }
    // Reflect typed query immediately so the banner shows "Thinking…"
    setSearchQuery(q);
    setSearching(true);
    debounceRef.current = setTimeout(() => runSearch(q), 500);
  };

  /** Optimistic ingest wrapper.
   *  Pushes a placeholder card immediately, runs the API, replaces with the
   *  real doc on success or marks it as failed (kept visible) on error. */
  const optimisticIngest = useCallback(
    async ({ source_type, title, category_id, project_id, preview, reason, apiCall }) => {
      const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const placeholder = {
        id: tempId,
        title: title || t('common.untitled'),
        source_type,
        category_id: category_id || null,
        project_id: project_id || null,
        reason: reason || '',
        status: 'processing',
        created_at: new Date().toISOString(),
        preview: preview || null,
      };
      setPendingDocs((prev) => [placeholder, ...prev]);
      try {
        const real = await apiCall();
        // Optimistic: prepend the real record so the user sees it INSTANTLY
        setDocs((prev) => {
          const exists = prev.some((d) => d.id === real.id);
          return exists
            ? prev
            : [{ ...real, id: real.id || real._id }, ...prev];
        });
        bumpCount(real.source_type, 1);
        setPendingDocs((prev) => prev.filter((d) => d.id !== tempId));
        // No re-list here: that would reset the feed to page 1 and drop every
        // page the user scrolled through. The processing poll flips the card
        // to ready on its own.
      } catch (e) {
        setPendingDocs((prev) =>
          prev.map((d) =>
            d.id === tempId
              ? { ...d, status: 'failed', error: e?.response?.data?.detail || e.message }
              : d,
          ),
        );
      }
    },
    [bumpCount, t],
  );

  // Combined note: text + optional link + images + PDFs in one request.
  const handleCreateNote = async ({
    body,
    title,
    links = [],
    categoryId,
    projectId,
    images = [],
    pdfs = [],
    audios = [],
  }) => {
    setBusy(true);
    try {
      const previewTitle =
        title ||
        (body || '').trim().split('\n', 1)[0].slice(0, 80) ||
        links[0] ||
        pdfs[0]?.name ||
        images[0]?.name ||
        audios[0]?.name ||
        t('common.note');
      await optimisticIngest({
        source_type: 'note',
        title: previewTitle,
        category_id: categoryId,
        project_id: projectId,
        reason: body,
        apiCall: () => ingestNote({ body, title, links, categoryId, projectId, images, pdfs, audios }),
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (doc) => {
    // For a failed-placeholder, "delete" just dismisses it client-side.
    if (doc.status === 'failed' || doc.id?.startsWith?.('pending-')) {
      setPendingDocs((prev) => prev.filter((d) => d.id !== doc.id));
      return;
    }
    if (!window.confirm(t('home.deleteConfirm', { title: doc.title }))) return;
    try {
      await deleteDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      bumpCount(doc.source_type, -1);
      if (searchResults) setSearchResults((prev) => prev.filter((r) => r.doc_id !== doc.id));
      setOpenedDoc(null);
    } catch (e) {
      alert(t('home.deleteFailed', { error: e?.response?.data?.detail || e.message }));
    }
  };

  // Decide which docs to render — pending placeholders ALWAYS show on top
  // regardless of filter so the user sees their just-submitted note immediately.
  const displayedDocs = useMemo(() => {
    let list = docs;
    if (searchResults !== null) {
      const byId = new Map(docs.map((d) => [d.id, d]));
      list = searchResults.map((r) => ({ ...(byId.get(r.doc_id) || {}), ...r, id: r.doc_id }));
    }
    let filtered = list;
    if (filter !== 'all') {
      if (filter.startsWith('cat:')) {
        // During a search the backend already pre-filtered by category, and
        // its results carry `category_id` from vector metadata. Re-filtering
        // here would only ever throw away rows the server deliberately kept.
        if (searchResults === null) {
          const cid = filter.slice(4);
          filtered = list.filter((d) => d.category_id === cid);
        }
      } else if (filter.startsWith('proj:')) {
        if (searchResults === null) {
          const pid = filter.slice(5);
          filtered = list.filter((d) => d.project_id === pid);
        }
      } else {
        filtered = list.filter((d) => d.source_type === filter);
      }
    }
    return [...pendingDocs, ...filtered];
  }, [searchResults, docs, filter, pendingDocs]);

  return (
    <div
      className="h-screen flex flex-col bg-keep-page text-keep-text overflow-hidden"
      onPointerDown={handleGridPointerDown}
      onClickCapture={handleGridClickCapture}
    >
      {/* Both headers stay mounted and simply cross-fade — swapping the
          <header> outright made the toolbar snap in/out instantly. They
          share this single sticky wrapper (rather than each being sticky on
          its own) so there's exactly one box occupying the layout slot,
          with the inactive one absolutely stacked on top of it. */}
      <div className="relative h-14 md:h-16 sticky top-0 z-30 bg-keep-topbar border-b border-keep-topbarBorder">
        <div
          className={`absolute inset-0 transition-opacity duration-200 ${
            selectMode ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          data-testid="selection-toolbar"
        >
          <div className="h-14 md:h-16 px-3 md:px-4 flex items-center gap-3">
            <button
              onClick={clearSelection}
              className="p-1.5 rounded-full hover:bg-keep-hover"
              aria-label={t('home.cancelSelection')}
              data-testid="selection-cancel"
            >
              <X className="w-4.5 h-4.5 text-keep-textMuted" />
            </button>
            <span className="text-[13px] text-keep-text">{t('home.selectedCount', { n: selectedIds.size })}</span>
            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={handleBulkShare}
                className="p-2 rounded-full hover:bg-keep-hover"
                aria-label={t('home.shareSelected')}
                title={bulkCopied ? t('common.linkCopied') : t('home.shareSelected')}
                data-testid="bulk-share"
              >
                {bulkCopied ? (
                  <Check className="w-4.5 h-4.5 text-keep-accentDark" />
                ) : (
                  <Share2 className="w-4.5 h-4.5 text-keep-textMuted" />
                )}
              </button>
              <button
                onClick={handleBulkDelete}
                className="p-2 rounded-full hover:bg-keep-hover"
                aria-label={t('home.deleteSelected')}
                data-testid="bulk-delete"
              >
                <Trash2 className="w-4.5 h-4.5 text-keep-textMuted" />
              </button>
            </div>
          </div>
        </div>
        <div
          className={`absolute inset-0 transition-opacity duration-200 ${
            selectMode ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
        >
          <TopBar
            onSearch={onSearch}
            onRefresh={loadDocs}
            busy={loading}
            onToggleSidebar={() => setSidebarOpen((v) => !v)}
            searching={searching}
          />
        </div>
      </div>

      {dragBox && (
        <div
          className="fixed z-40 bg-keep-accent/10 border border-keep-text/40 rounded-sm pointer-events-none"
          style={{
            left: dragBox.left,
            top: dragBox.top,
            width: dragBox.right - dragBox.left,
            height: dragBox.bottom - dragBox.top,
          }}
        />
      )}

      <div className="flex flex-1 min-h-0">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          currentFilter={filter}
          onFilterChange={(f) => {
            setFilter(f);
            setSidebarOpen(false);
            // A live search was scoped to the OLD category/project — re-run it
            // against the new one. `filter` is still stale in this closure, so
            // the new scope has to be passed explicitly.
            if (searchQuery) runSearch(searchQuery, categoryIdOf(f), projectIdOf(f));
          }}
          sourceCounts={sourceCounts}
          categories={categories}
          onCategoriesChanged={loadCategories}
          projects={projects}
          onProjectsChanged={loadProjects}
        />

        <main className="flex-1 min-w-0 keep-scroll overflow-y-auto" data-testid="main">
          <div className="hidden md:block">
            <TakeNote
              ref={desktopTakeNoteRef}
              onCreateNote={handleCreateNote}
              busy={busy}
              categories={categories}
              onCategoriesChanged={loadCategories}
              projects={projects}
              onProjectsChanged={loadProjects}
            />
          </div>


          <div className="px-3 md:px-4 pt-4 md:pt-0 pb-28 md:pb-12 max-w-[1400px] mx-auto">
            {displayedDocs.length === 0 ? (
              loading ? (
                <EmptyState message={t('home.loading')} />
              ) : searchResults !== null ? (
                <EmptyState message={t('home.noMatches')} hint={t('home.noMatchesHint')} />
              ) : filter !== 'all' ? (
                <EmptyState message={t('home.emptyFilter')} hint={t('home.emptyFilterHint')} />
              ) : (
                <EmptyState message={t('home.emptyTitle')} hint={t('home.emptyHint')} />
              )
            ) : (
              <div className="masonry masonry-responsive" data-testid="notes-grid">
                {displayedDocs.map((doc) => (
                  <NoteCard
                    key={doc.id}
                    doc={doc}
                    categories={categories}
                    projects={projects}
                    onOpen={doc.status === 'processing' || doc.status === 'failed' ? () => {} : setOpenedDoc}
                    onDelete={handleDelete}
                    highlight={searchQuery}
                    selected={selectedIds.has(doc.id)}
                    selectMode={selectMode}
                    onToggleSelect={toggleSelect}
                  />
                ))}
              </div>
            )}
            {searchResults === null && nextCursor ? (
              <div
                ref={sentinelRef}
                className="py-6 text-center text-[12px] text-keep-textMuted"
                data-testid="feed-load-more"
              >
                {loadingMore ? t('home.loadingMore') : ''}
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {!sidebarOpen && !selectMode && (
        <BottomBar
          onSearch={onSearch}
          onCompose={(rect) => {
            setComposeOrigin(rect);
            setComposeOpen(true);
          }}
          searching={searching}
        />
      )}

      {composeOpen && (
        <NoteComposeModal
          onClose={() => setComposeOpen(false)}
          origin={composeOrigin}
          onCreateNote={handleCreateNote}
          busy={busy}
          categories={categories}
          onCategoriesChanged={loadCategories}
          projects={projects}
          onProjectsChanged={loadProjects}
        />
      )}

      {openedDoc && (
        <NoteDetailModal
          doc={openedDoc}
          categories={categories}
          projects={projects}
          onClose={() => setOpenedDoc(null)}
          onDelete={handleDelete}
          onUpdated={(updated) => {
            setDocs((prev) => prev.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)));
            setOpenedDoc((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
          }}
        />
      )}

      {dragActive && (
        <div
          className="fixed inset-0 z-[70] bg-keep-accent/10 border-4 border-dashed border-keep-accent pointer-events-none flex items-center justify-center"
          data-testid="drop-overlay"
        >
          <div className="bg-keep-surface rounded-2xl shadow-keepHover px-8 py-6 flex flex-col items-center gap-2 text-center">
            <UploadCloud className="w-10 h-10 text-keep-accent" />
            <p className="text-[16px] font-medium text-keep-text">{t('home.dropTitle')}</p>
            <p className="text-[13px] text-keep-textMuted">{t('home.dropSubtitle')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Gate() {
  const { user, loading, isConfigured } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-keep-page">
        <div
          className="w-10 h-10 border-2 border-keep-accent border-t-transparent rounded-full animate-spin"
          data-testid="auth-loading"
        />
      </div>
    );
  }
  if (isConfigured && !user) return <LoginScreen />;
  return <AppShell />;
}

// Theme + language wrap every route (public share pages included) so a
// viewer's saved preference applies everywhere.
function Routes() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (path === '/auth/callback') {
    return <AuthCallback />;
  }
  // Checked before the single-note pattern below, since "collection" would
  // otherwise be captured as a doc id by the more permissive regex.
  const collectionMatch = path.match(/^\/n\/([^/]+)\/collection\/?$/);
  if (collectionMatch) {
    const ids = (new URLSearchParams(window.location.search).get('ids') || '')
      .split(',')
      .filter(Boolean);
    return <PublicCollectionView owner={decodeURIComponent(collectionMatch[1])} ids={ids} />;
  }
  const shareMatch = path.match(/^\/n\/([^/]+)\/([^/]+)\/?$/);
  if (shareMatch) {
    return (
      <PublicNoteView owner={decodeURIComponent(shareMatch[1])} docId={decodeURIComponent(shareMatch[2])} />
    );
  }
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <Routes />
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
